import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useTriggerLoadWithInput } from "@redotech/react-util/load";
import {
  ReviewForgeElementType,
  StaticElementType,
  SubmittableElementType,
  ThankYouUpsellElementType,
} from "@redotech/redo-model/forge/review-forge-types";
import { memo, useContext, useState } from "react";
import { BrandKitButton } from "../brand-kit-button";
import { Flex } from "../flex";
import { BrandKitContext } from "./review-form-editor-contexts";
import { PaginationButtons } from "./review-form-preview";
import * as reviewFormPreviewCss from "./review-form-preview.module.css";
import {
  PageValue,
  PossibleSubmittableData,
  ReviewElementProps,
  ReviewsFormValue,
  ThankYouPageValue,
  WriteableReviewElementProps,
  elementIsWriteable,
} from "./review-form-types";
import { ReviewsDiscount } from "./widgets/review-discount";
import { ReviewsLogo } from "./widgets/review-logo";
import { ReviewMediaUpload } from "./widgets/review-media-upload";
import { ReviewsParagraph } from "./widgets/review-paragraph";
import { ReviewsProductDetails } from "./widgets/review-product-details";
import { RichText } from "./widgets/review-rich-text";
import { ReviewsShortAnswer } from "./widgets/review-short-answer";
import { ReviewSingleSelect } from "./widgets/review-single-select";
import { ReviewsStars } from "./widgets/review-stars";

const staticPageElements: Record<
  StaticElementType,
  React.FC<ReviewElementProps<any>>
> = {
  [ReviewForgeElementType.RICH_TEXT]: RichText,
  [ReviewForgeElementType.LOGO]: ReviewsLogo,
  [ReviewForgeElementType.PRODUCT_DETAILS]: ReviewsProductDetails,
};

const submittablePageElements: Record<
  SubmittableElementType,
  React.FC<WriteableReviewElementProps<any, any>>
> = {
  [ReviewForgeElementType.SHORT_ANSWER]: ReviewsShortAnswer,
  [ReviewForgeElementType.PARAGRAPH]: ReviewsParagraph,
  [ReviewForgeElementType.STARS]: ReviewsStars,
  [ReviewForgeElementType.MEDIA_UPLOAD]: ReviewMediaUpload,
  [ReviewForgeElementType.SINGLE_SELECT]: ReviewSingleSelect,
};

const thankYouPageElements: Record<
  ThankYouUpsellElementType,
  React.FC<ReviewElementProps<any>>
> = {
  ...staticPageElements,
  [ReviewForgeElementType.DISCOUNT]: ReviewsDiscount,
  // [ReviewForgeElementType.UPSELL]: ReviewsUpsell,
  // [ReviewForgeElementType.START_ANOTHER_REVIEW]: ReviewsStartAnotherReview,
};

export interface SubmitAction {
  triggerSubmit(): void;
  state: LoadState<"success" | undefined>;
}

export const ReviewFormPreviewContainer = memo(
  function ReviewFormPreviewContainer({ form }: { form: ReviewsFormValue }) {
    const [submitTriggerAction, triggerSubmit] = useTriggerLoadWithInput(
      async (formData: Record<string, PossibleSubmittableData>) => {
        return "success" as const;
      },
    );

    const submitAction = {
      triggerSubmit: (formData: Record<string, PossibleSubmittableData>) =>
        triggerSubmit(formData),
      state: submitTriggerAction,
    };

    return (
      <Flex className={reviewFormPreviewCss.formPreviewWrapper} py="4xl">
        <ReviewForm
          form={form}
          handleClose={() => {}}
          submitAction={submitAction}
        />
      </Flex>
    );
  },
);

export const ReviewForm = memo(function ReviewForm({
  form,
  submitAction,
  handleClose,
}: {
  form: ReviewsFormValue;
  submitAction: {
    triggerSubmit(formData: Record<string, PossibleSubmittableData>): void;
    state: LoadState<"success" | undefined>;
  };
  handleClose(): void;
}) {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const [formData, setFormData] = useState<
    Record<string, PossibleSubmittableData>
  >({});

  const currentPage = form.pages[currentPageIndex];

  function handlePageNext() {
    if (currentPageIndex === form.pages.length - 1) {
      return;
    } else {
      setCurrentPageIndex((prev) => prev + 1);
    }
  }

  function handlePagePrevious() {
    if (currentPageIndex === 0) {
      return;
    } else {
      setCurrentPageIndex((prev) => prev - 1);
    }
  }

  const submitFormData = {
    triggerSubmit: () => submitAction.triggerSubmit(formData),
    state: submitAction.state,
  };

  return (
    <FormPageWrapper>
      {submitAction.state.value === "success" ? (
        <ThankYouPageContent
          handleClose={handleClose}
          thankYouPage={form.thankYouPage}
        />
      ) : (
        <Page
          addToFormData={(semanticTagId, data) => {
            setFormData((prev) => ({ ...prev, [semanticTagId]: data }));
          }}
          currentPage={currentPageIndex}
          formData={formData}
          maxPageIndex={form.pages.length - 1}
          next={handlePageNext}
          page={currentPage}
          previous={handlePagePrevious}
          submitAction={submitFormData}
        />
      )}
    </FormPageWrapper>
  );
});

export const Page = memo(function Page({
  page,
  currentPage,
  maxPageIndex,
  formData,
  addToFormData,
  next,
  previous,
  submitAction,
}: {
  page: PageValue;
  currentPage: number;
  maxPageIndex: number;
  formData: Record<string, PossibleSubmittableData>;
  addToFormData: (semanticTagId: string, data: PossibleSubmittableData) => void;
  next(): void;
  previous(): void;
  submitAction: SubmitAction;
}) {
  return (
    <PageContent
      addToFormData={addToFormData}
      currentPage={currentPage}
      formData={formData}
      maxPageIndex={maxPageIndex}
      next={next}
      page={page}
      previous={previous}
      submitAction={submitAction}
    />
  );
});

const FormPageWrapper = memo(function FormPageWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  const brandKit = useContext(BrandKitContext);
  return (
    <Flex
      className={reviewFormPreviewCss.formPageWrapper}
      dir="column"
      justify="space-between"
      px="7xl"
      py="4xl"
      style={{
        backgroundColor: brandKit.colors.background,
        maxWidth: "100%",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      {children}
    </Flex>
  );
});

const PageContent = memo(function PageContent({
  page,
  currentPage,
  maxPageIndex,
  formData,
  addToFormData,
  next,
  previous,
  submitAction,
}: {
  page: PageValue;
  currentPage: number;
  maxPageIndex: number;
  formData: Record<string, PossibleSubmittableData>;
  addToFormData: (semanticTagId: string, data: PossibleSubmittableData) => void;
  next(): void;
  previous(): void;
  submitAction: SubmitAction;
}) {
  const [showRequired, setShowRequired] = useState(false);

  const writeableElements = page.elements.filter((element) =>
    elementIsWriteable(element),
  );

  const requiredWriteableElements = writeableElements.filter((element) => {
    return element.required;
  });

  const writeableElementsAllSet = requiredWriteableElements.every((element) => {
    return formData[element.tag.tagId] !== undefined;
  });

  const handleNext = useHandler(() => {
    if (!writeableElementsAllSet) {
      setShowRequired(true);
    } else {
      setShowRequired(false);
      next();
    }
  });

  const handleSubmitAction = useHandler(() => {
    if (!writeableElementsAllSet) {
      setShowRequired(true);
    } else {
      setShowRequired(false);
      submitAction.triggerSubmit();
    }
  });

  return (
    <Flex dir="column" gap="xl">
      <Flex dir="column" gap="3xl">
        {page.elements.map((element, elementIndex) => {
          const PageElement = elementIsWriteable(element)
            ? submittablePageElements[element.type]
            : staticPageElements[element.type];
          const props = {
            element,
            setData: (data: PossibleSubmittableData) => {
              if (elementIsWriteable(element)) {
                addToFormData(element.tag.tagId, data);
              }
            },
            data: elementIsWriteable(element)
              ? formData[element.tag.tagId]
              : undefined,
            showRequired: showRequired,
          };
          return <PageElement {...props} key={elementIndex} />;
        })}
      </Flex>
      <PaginationButtons
        handleNextClick={handleNext}
        handlePreviousClick={previous}
        index={currentPage}
        maxIndex={maxPageIndex}
        pending={submitAction.state.pending}
        submit={handleSubmitAction}
      />
    </Flex>
  );
});

const ThankYouPageContent = memo(function ThankYouPageContent({
  thankYouPage,
  handleClose,
}: {
  thankYouPage: ThankYouPageValue;
  handleClose(): void;
}) {
  const brandKit = useContext(BrandKitContext);

  return (
    <Flex dir="column" gap="xl">
      <Flex dir="column" gap="3xl">
        {thankYouPage.elements.map((element, elementIndex) => {
          const PageElement = thankYouPageElements[element.type];
          const props = { element };
          return <PageElement {...props} key={elementIndex} />;
        })}
      </Flex>
      <BrandKitButton
        brandKit={brandKit}
        hierarchy="primary"
        onClick={handleClose}
        text="Close"
      />
    </Flex>
  );
});
